// The tapped machine card flies up into the payment flow's header and turns
// into its machine badge, so it's obvious that the header shows the machine
// being paid for (people do end up paying for the wrong machine).
//
// Two copies fly along the same path: the card shrinks into the badge's spot
// and fades out while a copy of the badge grows out of the card and fades in.
// They keep the same size ratio all the way, so they read as one thing
// changing shape. The real badge stays hidden until they land, then the badge
// gives off two rings to say "here it is".

// strong ease-in-out: on-screen movement
const MOVE = "cubic-bezier(0.77, 0, 0.175, 1)";
// strong ease-out: the landing rings
const OUT = "cubic-bezier(0.23, 1, 0.32, 1)";
// long for UI on purpose: it happens once per purchase, and the point is that
// people see where the card goes
const FLIGHT_MS = 650;
const RING_MS = 700;

type Source = { card: HTMLElement; rect: DOMRect };

export type MachineMorph = ReturnType<typeof createMachineMorph>;

/** One per flow (keep it in state: `into` must keep its identity as a ref). */
export function createMachineMorph() {
  let source: Source | null = null;
  return {
    /** Lifts the tapped card, until the header's badge shows up. */
    from(card: HTMLElement) {
      source = { card: copyOf(card), rect: card.getBoundingClientRect() };
    },
    /** The header badge's ref: flies the lifted card into it when it mounts. */
    into(badge: HTMLElement | null) {
      if (!badge || !source) return;
      const { card, rect } = source;
      source = null;
      void fly(card, rect, badge);
    },
  };
}

async function fly(card: HTMLElement, from: DOMRect, badge: HTMLElement) {
  const to = badge.getBoundingClientRect();
  if (prefersReducedMotion()) {
    // no flight: the badge fades in and still rings
    await badge.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 200,
      easing: OUT,
    }).finished;
    ring(badge, to);
    return;
  }
  const dx = center(to).x - center(from).x;
  const dy = center(to).y - center(from).y;
  // the card shrinks to fit in the badge; the badge starts as big as the card
  const scale = Math.min(to.width / from.width, to.height / from.height);
  const cardCopy = pin(card, from);
  const badgeCopy = pin(copyOf(badge), to);
  const timing = { duration: FLIGHT_MS, easing: MOVE, fill: "both" } as const;
  // the real one waits under the copies
  badge.animate([{ opacity: 0 }, { opacity: 0 }], { duration: FLIGHT_MS });
  await Promise.all([
    cardCopy.animate(
      [
        { transform: "none", opacity: 1 },
        { opacity: 1, offset: 0.25 },
        { opacity: 0, offset: 0.6 },
        {
          transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
          opacity: 0,
        },
      ],
      timing,
    ).finished,
    badgeCopy.animate(
      [
        {
          transform: `translate(${-dx}px, ${-dy}px) scale(${1 / scale})`,
          opacity: 0,
        },
        { opacity: 0, offset: 0.25 },
        { opacity: 1, offset: 0.6 },
        { transform: "none", opacity: 1 },
      ],
      timing,
    ).finished,
  ]).finally(() => {
    cardCopy.remove();
    badgeCopy.remove();
  });
  ring(badge, to);
}

/** Two white rings spreading out of the badge, a beat apart. */
function ring(badge: HTMLElement, rect: DOMRect) {
  const { borderRadius } = getComputedStyle(badge);
  for (const delay of [0, 200]) {
    const ring = pin(document.createElement("div"), rect);
    Object.assign(ring.style, { border: "6px solid white", borderRadius });
    ring
      .animate(
        [
          { transform: "scale(1)", opacity: 0.9 },
          { transform: "scale(1.5)", opacity: 0 },
        ],
        { duration: RING_MS, delay, easing: OUT, fill: "both" },
      )
      .finished.finally(() => ring.remove());
  }
}

/** A detached copy that looks the same outside its parents (text color, font). */
function copyOf(element: HTMLElement) {
  const copy = element.cloneNode(true) as HTMLElement;
  const { color, fontFamily } = getComputedStyle(element);
  Object.assign(copy.style, { color, fontFamily });
  return copy;
}

/** Puts the element over everything, exactly at `rect`. */
function pin(element: HTMLElement, rect: DOMRect) {
  Object.assign(element.style, {
    position: "fixed",
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: "0",
    boxSizing: "border-box",
    visibility: "visible",
    pointerEvents: "none",
    zIndex: "90",
  });
  document.body.append(element);
  return element;
}

function center(rect: DOMRect) {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
